import React, { FC, useEffect, useRef, useState } from 'react';
import { CircleCloseIcon, DeleteIcon } from 'assets/icons';
import { CustomModal } from 'components';
import PlusButton from 'components/plus-button/PlusButton';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import If from 'components/If/If';
import { BaseReminderModalProps, ReminderColumnConfig, ReminderConfig, ScheduleRelation } from './types';

interface ReminderModalBaseProps extends BaseReminderModalProps {
  columns: ReminderColumnConfig[];
}

const ReminderModalBase: FC<ReminderModalBaseProps> = ({
  isOpen,
  onClose,
  onSave,
  onCancel,
  isLoading,
  title = 'Reminders',
  subtitle = 'Configure reminders',
  initialReminders = [],
  maxReminders = 10,
  columns,
  showAddButton = true,
  showDeleteButton = true,
  renderValidationRow,
  scheduleRelationsPriority = [ScheduleRelation.BEFORE, ScheduleRelation.ON, ScheduleRelation.AFTER],
  onNewReminderCreate,
  footerInfo
}) => {
  const [reminders, setReminders] = useState<ReminderConfig[]>(initialReminders.length > 0 ? initialReminders : []);
  const prevIsOpenRef = useRef<boolean>(false);

  // Reset reminders state only when modal transitions from closed to open
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      // Modal just opened
      setReminders(initialReminders.length > 0 ? initialReminders : []);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, initialReminders]);

  const getNextScheduleRelation = (existingReminders: ReminderConfig[]): ScheduleRelation => {
    // Find the first schedule relation that doesn't exist yet
    const nextRelation = scheduleRelationsPriority.find(
      relation => !existingReminders.some(r => r.scheduleRelation === relation)
    );

    // If found, return it; otherwise cycle back to the first one
    return nextRelation || scheduleRelationsPriority[0];
  };

  const getNextDaysValue = (scheduleRelation: ScheduleRelation, existingReminders: ReminderConfig[]): number => {
    // ON always has 0 days
    if (scheduleRelation === ScheduleRelation.ON) {
      return 0;
    }

    // Find all reminders with the same schedule relation
    const sameLevelReminders = existingReminders.filter(r => r.scheduleRelation === scheduleRelation);

    // If no reminders exist with this relation, start with 1
    if (sameLevelReminders.length === 0) {
      return 1;
    }

    // Find the maximum days value and increment
    const maxDays = Math.max(...sameLevelReminders.map(r => r.days));
    return maxDays + 1;
  };

  const handleAddReminder = () => {
    if (reminders.length >= maxReminders) return;

    const nextScheduleRelation = getNextScheduleRelation(reminders);
    const nextDays = getNextDaysValue(nextScheduleRelation, reminders);

    const baseReminder: ReminderConfig = {
      id: Date.now().toString() + '-' + (reminders.length + 1),
      reminderNumber: reminders.length + 1,
      scheduleRelation: nextScheduleRelation,
      days: nextDays
    };

    // Allow parent component to enrich the reminder with additional calculated fields
    const newReminder = onNewReminderCreate ? onNewReminderCreate(baseReminder) : baseReminder;

    setReminders([...reminders, newReminder]);
  };

  const handleDeleteReminder = (id: string) => {
    const updatedReminders = reminders
      .filter(reminder => reminder.id !== id)
      .map((reminder, index) => ({
        ...reminder,
        reminderNumber: index + 1
      }));
    setReminders(updatedReminders);
  };

  const handleReminderChange = (id: string, field: string, value: unknown) => {
    setReminders(
      prev =>
        prev.map(reminder => {
          if (reminder.id === id) {
            return { ...reminder, [field]: value };
          }
          return reminder;
        })
      // eslint-disable-next-line function-paren-newline
    );
  };

  const handleSave = () => {
    onSave(reminders);
  };

  const handleCancel = () => {
    onCancel?.();
    onClose();
  };

  return (
    <CustomModal
      open={isOpen}
      closeModal={onClose}
      enableOutsideClick={false} //false as it effects other portal component within modal
      className="max-h-[90vh] max-w-[90%] md:w-min md:min-w-min"
      overlayClassName="bg-[#00132480]">
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6">
          <div>
            <h2 className="text-xl font-bold text-headingGray">{title}</h2>
            <p className="mt-1 text-sbase font-normal text-primaryText">{subtitle}</p>
          </div>
          <CircleCloseIcon onClick={onClose} className="cursor-pointer text-accentText hover:text-primaryText" />
        </div>

        {/* Content */}
        <div className="mx-6 flex-1 overflow-y-auto rounded border border-borderGray px-3 py-2.5">
          <div className="customNormalScroll max-h-[350px] min-h-[250px] overflow-y-auto">
            <table className="w-full table-fixed">
              <thead>
                <tr className="border-b-[1.5px] border-borderGray">
                  <th
                    className="w-[110px] py-3 px-2 text-left text-sm 
                  font-semibold text-primaryText">
                    Reminder
                  </th>
                  {columns.map(column => (
                    <th
                      key={column.key}
                      className="py-3 px-2 text-left text-sm font-semibold text-primaryText"
                      style={column.width ? { width: column.width } : {}}>
                      {column.label}
                    </th>
                  ))}
                  {showDeleteButton && <th className="w-10"></th>}
                </tr>
              </thead>
              <tbody>
                {reminders.length > 0 ? (
                  reminders.map(reminder => {
                    const validationContent = renderValidationRow?.(reminder);
                    return (
                      <React.Fragment key={reminder.id}>
                        <tr className={`${validationContent ? '' : 'border-b border-borderGray'}`}>
                          <td className="w-[110px] py-3 px-2">
                            <span className="text-sm font-medium text-primaryText">
                              Reminder {reminder.reminderNumber}
                            </span>
                          </td>

                          {columns.map(column => (
                            <td key={column.key} className="py-3 px-2">
                              {column.render(
                                reminder,
                                (field, value) => handleReminderChange(reminder.id, field, value)
                                // eslint-disable-next-line function-paren-newline
                              )}
                            </td>
                          ))}

                          <If condition={showDeleteButton}>
                            <td className="py-4 px-2">
                              <button
                                onClick={() => handleDeleteReminder(reminder.id)}
                                className="p-1 text-error hover:text-red-700"
                                type="button">
                                <DeleteIcon className="path path-fill-current h-4 w-4 text-accentText" />
                              </button>
                            </td>
                          </If>
                        </tr>
                        <If condition={!!validationContent}>
                          <tr className="border-b border-borderGray">
                            <td colSpan={columns.length + (showDeleteButton ? 2 : 1)} className="px-2 pb-2">
                              {validationContent}
                            </td>
                          </tr>
                        </If>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={columns.length + (showDeleteButton ? 2 : 1)} className="py-8 text-center">
                      <span className="text-sm text-accentText">No reminders available</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Add Reminder Button */}
          <If condition={showAddButton}>
            <PlusButton
              id="add-reminder-button"
              onClick={handleAddReminder}
              className="px-1 pt-3 pb-1"
              label="Add reminder"
              disabled={maxReminders ? reminders.length >= maxReminders : false}
            />
          </If>
        </div>

        <If condition={!!footerInfo}>
          <div className="mx-6 mt-2">{footerInfo}</div>
        </If>

        {/* Footer */}
        <div className="flex justify-end gap-4 p-6">
          <CustomButton
            id="reminder-modal-cancel"
            onClick={handleCancel}
            type={ButtonType.SECONDARY}
            className="w-[100px]">
            Cancel
          </CustomButton>
          <CustomButton
            id="reminder-modal-save"
            onClick={handleSave}
            type={ButtonType.PRIMARY}
            className="w-[100px]"
            isLoading={isLoading}>
            Save
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default ReminderModalBase;
